import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Button,
  Modal,
} from 'antd';
import {
  formatUnits,
  parseUnits,
} from 'ethers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { tokenArr } from '@/collocate';
import BalanceCom from '@/components/BalanceCom';
import Icon from '@/components/Icon';
import InputBoxFrom from '@/components/InputBoxFrom';
import InputBoxTo from '@/components/InputBoxTo';
import LpTokenImg from '@/components/LpTokenImg';
import PresentData from '@/components/PresentData';
import ScrollView from '@/components/ScrollView';
import Setting from '@/components/Setting';
import useAddLiquidity from '@/hooks/useAddLiquidity';
import useAddLiquidityStaticCall from '@/hooks/useAddLiquidityStaticCall';
import useBestTradeExactIn from '@/hooks/useBestTradeExactIn';
import useErc20Contract from '@/hooks/useErc20Contract';
import useGetAllowanceToken from '@/hooks/useGetAllowanceToken';
import useGetTokenBalance from '@/hooks/useGetTokenBalance';
import useLpAddress from '@/hooks/useLpAddress';
import useLpBalanceOf from '@/hooks/useLpBalanceOf';
import useLpContract from '@/hooks/useLpContract';
import useLpTotalSupply from '@/hooks/useLpTotalSupply';
import useControllablePolling from '@/hooks/usePollingEffect';
import useQuote from '@/hooks/useQuote';
import add from '@/image/add.svg';
import useTransactionSetting from '@/store/transactionSetting';
import useConnectWallet from '@/store/wallet/useConnectWallet';
import useWalletPop from '@/store/walletPop';
import {
  divideDecimal,
  formatTo6Decimals,
  multiplyDecimal,
  subtractDecimal,
} from '@/utils';

import MyLp from './conponent/MyLp';

export default function Mobility() {
    const { t } = useTranslation()
    // 路由跳转
    const router = useNavigate()
    // 获取全局交易设置中的流动性滑点和交易时限
    const {
        transactionSetting: { mobilitySlippage, mobilityTradingHour },
    } = useTransactionSetting();

    // 添加流动性操作，成功后刷新余额和授权等
    const { addLiquidity, addLiquidityLod } = useAddLiquidity(() => {
        fromBalance.getTokenBalance();
        toBalance.getTokenBalance();
        fromAllowance.getAllowance();
        toAllowance.getAllowance();
        setFromAmount('');
        setToAmount('');
        getBalanceLp();
    });

    // 钱包弹窗控制
    const { setOpenWallet } = useWalletPop();
    // 钱包地址
    const { address } = useConnectWallet();

    // 控制确认弹窗的开关
    const [open, setOpen] = useState(false);

    // 用户输入的数量
    const [fromAmount, setFromAmount] = useState<string>('');
    const [toAmount, setToAmount] = useState<string>('');

    // 选中的代币索引
    const [fromTokenIndex, setFromTokenIndex] = useState<number>(1);
    const [toTokenIndex, setToTokenIndex] = useState<number>(-1);

    // 计算 from/to 代币的合约地址、symbol、logo
    const fromTokenAddress = useMemo(() => {
        return fromTokenIndex < 0 ? '' : tokenArr[fromTokenIndex].address
    }, [fromTokenIndex]);
    const toTokenAddress = useMemo(() => {
        return toTokenIndex < 0 ? '' : tokenArr[toTokenIndex].address
    }, [toTokenIndex]);
    const fromTokenSymbol = useMemo(() => {
        return fromTokenIndex < 0 ? '' : tokenArr[fromTokenIndex].symbol
    }, [fromTokenIndex]);
    const toTokenSymbol = useMemo(() => {
        return toTokenIndex < 0 ? '' : tokenArr[toTokenIndex].symbol
    }, [toTokenIndex]);
    const fromTokenLogo = useMemo(() => {
        return fromTokenIndex < 0 ? '' : tokenArr[fromTokenIndex].logo
    }, [fromTokenIndex]);
    const toTokenLogo = useMemo(() => {
        return toTokenIndex < 0 ? '' : tokenArr[toTokenIndex].logo
    }, [toTokenIndex]);
    // 获取 LP 地址
    const { getLpAddressLod, lpAddress } = useLpAddress(fromTokenAddress, toTokenAddress);

    // LP 合约实例
    const lpContract = useLpContract(lpAddress);

    // 获取我的 LP 余额
    const { balanceLp, balanceLpLod, getBalanceLp } = useLpBalanceOf(lpContract);

    // 获取 LP 总供应量
    const { totalSupply } = useLpTotalSupply(lpContract);

    // 是否反向报价（from→to 还是 to→from）
    const [isQuoteReverse, setIsQuoteReverse] = useState(false);

    // 获取报价
    const { quoteLod, getQuote, amountOut } = useQuote(
        lpContract,
        !isQuoteReverse ? fromTokenAddress : toTokenAddress,
        isQuoteReverse ? fromTokenAddress : toTokenAddress,
        !isQuoteReverse ? (fromAmount ? parseUnits(fromAmount) : BigInt(0)) : (toAmount ? parseUnits(toAmount) : BigInt(0))
    );

    // ERC20 合约实例
    const contractTokenFrom = useErc20Contract(fromTokenAddress);
    const contractTokenTo = useErc20Contract(toTokenAddress);

    // 获取授权额度和授权操作
    const fromAllowance = useGetAllowanceToken(contractTokenFrom);
    const toAllowance = useGetAllowanceToken(contractTokenTo);

    // 获取代币余额
    const fromBalance = useGetTokenBalance(contractTokenFrom);
    const toBalance = useGetTokenBalance(contractTokenTo);

    // 计算添加流动性所需参数
    const addLiquidityData = useMemo(() => {
        const slippage = divideDecimal(mobilitySlippage, 100);
        const amountFromMin = parseUnits(formatTo6Decimals(multiplyDecimal(fromAmount || '0', subtractDecimal(1, slippage)), 18));
        const amountToMin = parseUnits(formatTo6Decimals(multiplyDecimal(toAmount || '0', subtractDecimal(1, slippage)), 18));
        const newtFromAmount = parseUnits(fromAmount || '0');
        const newtToAmount = parseUnits(toAmount || '0');
        // 交易截止时间（分钟转合约单位）
        const deadline = parseUnits(multiplyDecimal(mobilityTradingHour, 60));
        return {
            fromTokenAddress,
            toTokenAddress,
            newtFromAmount,
            newtToAmount,
            amountFromMin,
            amountToMin,
            deadline
        }
    }, [fromAmount, fromTokenAddress, mobilitySlippage, mobilityTradingHour, toAmount, toTokenAddress]);

    // 预计可获得的 LP 数量
    const { liquidityStaticCall, addLiquidityStaticCallLod, addLiquidityStaticCall } = useAddLiquidityStaticCall(
        addLiquidityData.fromTokenAddress,
        addLiquidityData.toTokenAddress,
        addLiquidityData.newtFromAmount,
        addLiquidityData.newtToAmount,
        addLiquidityData.amountFromMin,
        addLiquidityData.amountToMin,
        addLiquidityData.deadline
    );

    // 添加流动性操作
    const addLiquidityFun = () => {

        addLiquidity(
            addLiquidityData.fromTokenAddress,
            addLiquidityData.toTokenAddress,
            addLiquidityData.newtFromAmount,
            addLiquidityData.newtToAmount,
            addLiquidityData.amountFromMin,
            addLiquidityData.amountToMin,
            addLiquidityData.deadline,
            { success: t('mobility.addLiquiditySuccess'), error: t('mobility.addLiquidityFailed') }
        );
        handleCancel(); // 关闭弹窗
    };
    // 获取费率
    const fromTo = useBestTradeExactIn(fromTokenAddress, toTokenAddress, parseUnits('1')); // 获取兑换比例 hook
    const toFrom = useBestTradeExactIn(toTokenAddress, fromTokenAddress, parseUnits('1')); // 获取兑换比例 hook
    // 计算池子份额
    const lpShare = useMemo(() => {
        const newTotalSupply = formatUnits(liquidityStaticCall + totalSupply);
        const newLpAmount = formatUnits(liquidityStaticCall);
        if (newTotalSupply === newLpAmount) return "100"
        return (multiplyDecimal(divideDecimal(newLpAmount, newTotalSupply) || 0, 100));
    }, [liquidityStaticCall, totalSupply]);
    /**
     * 输入数量变化时调用
     * @param val 用户输入的数量字符串
     * @param index 0为from输入框，1为to输入框
     */
    const handleAmountChange = async (val: string, index: 0 | 1) => {
        const setFun1 = [setFromAmount, setToAmount];
        // 如果代币未选择，则仅更新对应输入框的数量，不触发兑换量计算
        const is = fromTokenAddress && toTokenAddress;
        setFun1[index](val);
        if (!is) return;
        if (lpAddress) {
            setIsQuoteReverse(!!index); // 标记当前输入框
        }
    };
    /**
     * 处理代币选择变化
     * @param val 选中的代币索引
     * @param index 0 表示 from 代币，1 表示 to 代币
     */
    const handleTokenChange = async (val: number, index: 0 | 1) => {
        const setFunTokenVal = [setFromTokenIndex, setToTokenIndex];
        // 正常选择代币流程
        if (val === fromTokenIndex || val === toTokenIndex) return;
        setFunTokenVal[index](val);
    };

    /**
     * 兑换按钮点击事件
     * @param type 1-连接钱包，2-授权，3-授权，4-添加流动性
     */
    const handleBtnClick = (type: number) => {
        const handlers = [
            setOpenWallet, // 打开钱包连接弹窗
            () => fromAllowance.approve({ success: t('mobility.approvalSuccess'), error: t('mobility.approvalFailed'), }), // 授权 from 代币
            () => toAllowance.approve({ success: t('mobility.approvalSuccess'), error: t('mobility.approvalFailed'), }),   // 授权 to 代币
            () => {
                fromTo.getBestTradePath()
                toFrom.getBestTradePath();
                addLiquidityStaticCall(); // 计算添加流动性所需的 LP 数量
                showModal(); // 显示确认弹窗
            }
        ];
        handlers[type - 1]?.();
    };

    // 显示确认弹窗
    const showModal = () => {
        setOpen(true);
    };
    // 关闭弹窗
    const handleCancel = () => {
        setOpen(false);
    };
    /**
     * 根据当前状态决定按钮显示文本、禁用状态和触发动作类型
     */
    const btnLodtext = useMemo(() => {
        if (!address) return { loading: false, text: t('mobility.connectWallet'), disabled: false, type: 1 };
        if (!fromTokenAddress || !toTokenAddress) return { loading: false, text: t('mobility.pleaseSelectToken'), disabled: true, type: 0 };
        if (getLpAddressLod) return { loading: getLpAddressLod, text: t('mobility.fetchingLiquidity'), disabled: true, type: 0 };
        if (!fromAmount) return { loading: false, text: t('mobility.pleaseEnterSwapAmount'), disabled: true, type: 0 };
        if (fromBalance.tokenBalanceLod) return { loading: true, text: t('mobility.fetchingBalance'), disabled: true, type: 0 };
        if (parseUnits(fromAmount || '0') > fromBalance.tokenBalance) return { loading: false, text: `${fromTokenSymbol}${t('mobility.insufficientBalance')}`, disabled: true, type: 0 };
        if (toBalance.tokenBalanceLod) return { loading: true, text: t('mobility.fetchingBalance'), disabled: true, type: 0 };
        if (parseUnits(toAmount || '0') > toBalance.tokenBalance) return { loading: false, text: `${toTokenSymbol}${t('mobility.insufficientBalance')}`, disabled: true, type: 0 };
        if (fromAllowance.allowanceLod) return { loading: true, text: t('mobility.updatingAllowance'), disabled: true, type: 0 };
        if (fromAllowance.allowanceLodFun) return { loading: true, text: t('mobility.approving'), disabled: true, type: 2 };
        if (fromAllowance.allowance < parseUnits(fromAmount || '0')) return { loading: false, text: `${fromTokenSymbol}${t('mobility.approve')}`, disabled: false, type: 2 };
        if (toAllowance.allowanceLod) return { loading: true, text: t('mobility.updatingAllowance'), disabled: true, type: 0 };
        if (toAllowance.allowanceLodFun) return { loading: true, text: t('mobility.approving'), disabled: true, type: 2 };
        if (toAllowance.allowance < parseUnits(toAmount || '0')) return { loading: false, text: `${toTokenSymbol}${t('mobility.approve')}`, disabled: false, type: 3 };
        if (quoteLod) return { loading: true, text: t('mobility.fetchingSwapAmount'), disabled: true, type: 0 };
        if (addLiquidityLod) return { loading: true, text: t('mobility.addingLiquidity'), disabled: false, type: 0 };
        return { loading: false, text: t('mobility.addLiquidity'), disabled: false, type: 4 };
    }, [address, t, fromTokenAddress, toTokenAddress, getLpAddressLod, fromAmount, fromBalance.tokenBalanceLod, fromBalance.tokenBalance, fromTokenSymbol, toBalance.tokenBalanceLod, toBalance.tokenBalance, toAmount, toTokenSymbol, fromAllowance.allowanceLod, fromAllowance.allowanceLodFun, fromAllowance.allowance, toAllowance.allowanceLod, toAllowance.allowanceLodFun, toAllowance.allowance, quoteLod, addLiquidityLod]);

    // 监听报价变化，自动填充输入框
    useEffect(() => {
        const amount = amountOut ? (formatUnits(amountOut)) : '';
        isQuoteReverse ? setFromAmount(amount) : setToAmount(amount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountOut]);

    // 轮询获取报价
    useControllablePolling(() => {
        getQuote();
    }, 10000, (!getLpAddressLod && lpAddress && !quoteLod && !addLiquidityLod), false);

    return (
        <ScrollView px="168px" className="px-4.25 mt-8 md:max-w-xl md:mx-auto md:h-auto md:mt-16">
            <>
                {/* 设置面板 */}
                <div className="mb-2.5">
                    <Setting type={1} />
                </div>

                <div className="relative">
                    {/* 输入框 - from */}
                    <div className="mb-5 md:mb-10">
                        <InputBoxFrom
                            onFocusF={() => setIsQuoteReverse(false)}
                            type={1}
                            inputLod={isQuoteReverse ? quoteLod : false}
                            tokenBalance={fromBalance.tokenBalance}
                            tokenBalanceLod={fromBalance.tokenBalanceLod}
                            tokenAddressIndex={fromTokenIndex}
                            getSelectedTokenIndex={(index) => handleTokenChange(index, 0)}
                            amountFrom={fromAmount}
                            setAmountFrom={(val) => handleAmountChange(val, 0)}
                        />
                    </div>

                    {/* 代币切换按钮 */}
                    <div className="w-7.5 h-7.5 bg-f7 rounded-full flex items-center justify-center absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <Icon className="w-4 cursor-pointer" src={add} />
                    </div>

                    {/* 输入框 - to */}
                    <InputBoxTo
                        onFocusF={() => setIsQuoteReverse(true)}
                        type={1}
                        inputLod={isQuoteReverse ? false : quoteLod}
                        tokenBalance={toBalance.tokenBalance}
                        tokenBalanceLod={toBalance.tokenBalanceLod}
                        tokenAddressIndex={toTokenIndex}
                        getSelectedTokenIndex={(index) => handleTokenChange(index, 1)}
                        amountTo={toAmount}
                        setAmountFrom={(val) => handleAmountChange(val, 1)}
                    />
                </div>

                {/* 操作按钮 */}
                <Button
                    className="mt-4 h-12.5 rounded-10 mb-7.5 md:mt-10"
                    disabled={btnLodtext.disabled}
                    loading={btnLodtext.loading}
                    block
                    onClick={() => handleBtnClick(btnLodtext.type)}
                >
                    {btnLodtext.text}
                </Button>

                {/* 我的LP信息展示 */}
                {lpAddress && address && <div onClick={() => router(`/myLp?lpAddress=${lpAddress}&tokenA=${fromTokenIndex}&tokenB=${toTokenIndex}`)}>
                    <MyLp
                        fromTokenLogo={fromTokenLogo}
                        toTokenLogo={toTokenLogo}
                        fromTokenSymbol={fromTokenSymbol}
                        toTokenSymbol={toTokenSymbol}
                        balanceLp={balanceLp}
                        balanceLpLod={balanceLpLod}
                    />
                </div>}

                {/* 添加流动性确认弹窗 */}
                <Modal
                    centered
                    keyboard
                    closeIcon={false}
                    open={open}
                    onCancel={handleCancel}
                    footer={null}
                    className='w-85.75'
                >
                    <div className='text-mainBlack p-3.75 text-sm '>
                        <div className='font-medium '>{t('mobility.youWillReceive')}</div>
                        <div className='flex items-center mb-2.5'>
                            <BalanceCom className='mr-1.25' balance={liquidityStaticCall} loading={addLiquidityStaticCallLod} />

                            <LpTokenImg className='w-6' fromTokenLogo={fromTokenLogo} toTokenLogo={toTokenLogo} />
                        </div>
                        <div className='pb-2.5'>
                            {fromTokenSymbol} / {toTokenSymbol}{t('mobility.poolToken')}
                        </div>
                        <p className='text-xs text-a8 pb-5.5'>{t('mobility.outputEstimateNote')}{mobilitySlippage}%，{t('mobility.txRevertOnPriceChange')}</p>
                        <div className='flex items-center justify-between mb-3'>
                            <div>{fromTokenSymbol}{t('mobility.deposit')}</div>
                            <div className='flex items-center'>
                                <img className='w-6.25 mr-1.25' src={fromTokenLogo} alt="" />
                                <span>{fromAmount}</span>
                            </div>
                        </div>
                        <div className='flex items-center justify-between mb-3'>
                            <div>{toTokenSymbol}{t('mobility.deposit')}</div>
                            <div className='flex items-center'>
                                <img className='w-6.25 mr-1.25' src={toTokenLogo} alt="" />
                                <span>{toAmount}</span>
                            </div>
                        </div>
                        <div className='flex items-start justify-between mb-3'>
                            <span>{t('mobility.rate')}</span>
                            <div>
                                <div className='flex items-center'>1 {toTokenSymbol} = &nbsp; <BalanceCom balance={toFrom.bestTrade.amountOut} text={` ${fromTokenSymbol}`} loading={toFrom.bestTradeExactInLod} /></div>
                                <div className='flex items-center'>1 {fromTokenSymbol} =  &nbsp; <BalanceCom balance={fromTo.bestTrade.amountOut} text={` ${toTokenSymbol}`} loading={fromTo.bestTradeExactInLod} /></div>
                            </div>
                        </div>
                        <div className='flex items-center justify-between mb-7.5'>
                            <span>{t('mobility.poolShare')}</span>
                            <PresentData text='%' value={lpShare as string} loading={addLiquidityStaticCallLod} />
                        </div>
                        <Button onClick={() => addLiquidityFun()} className='h-12.5 rounded-10' block>{t('mobility.confirm')}</Button>
                    </div>
                </Modal>
            </>
        </ScrollView>
    );
}